import Chip from "@mui/material/Chip";
import { Theme, alpha } from "@mui/material/styles";
import CircleIcon from "@mui/icons-material/Circle";
import { SaleCenterClosingStatus } from "../../types/sale-center-closing.type";
import {
  CLOSING_STATUS_LABEL,
  CLOSING_STATUS_SHORT_LABEL,
} from "./saleCenterClosing.utils";

/**
 * The colour each state is drawn in.
 *
 * `open` is deliberately neutral: a branch-day that simply has not been closed yet is not a
 * problem, and colouring it would make every unfinished day shout as loudly as the one that
 * was actually handed back. `closed` is the settled state and `sent_back` is the one asking
 * the branch for something, which is what warning means everywhere else in this report.
 */
const STATUS_COLOR: Record<
  SaleCenterClosingStatus,
  (theme: Theme) => string
> = {
  open: (theme) => theme.palette.gray.main,
  closed: (theme) => theme.palette.success.dark,
  sent_back: (theme) => theme.palette.warning.dark,
};

/** Enough of the status colour to outline and seat the chip without filling it in. */
const BORDER_OPACITY = 0.4;
const SURFACE_OPACITY = 0.12;

/** The leading dot, in px: the state readable before any of the words are. */
const STATUS_DOT_SIZE = 10;

interface ClosingStatusChipProps {
  status: SaleCenterClosingStatus;
  /**
   * Spells the state out in full (ยังไม่ปิดศูนย์เซล). For the ปิดศูนย์เซล panel, where the chip
   * is the only thing naming the domain; the summary column says it in its own header and
   * takes the short form.
   */
  spelledOut?: boolean;
}

/**
 * สถานะการปิดยอด as a status pill — a coloured dot, the state, and a tinted outline.
 *
 * Shared by the branch-day summary column and the ปิดศูนย์เซล panel's header so the same
 * state never reads as two different things on the two screens. The column is scanned rather
 * than read — the supervisor is looking for the branch-days still to act on down a list of
 * twenty — and the dot answers that before the words do.
 */
export default function ClosingStatusChip({
  status,
  spelledOut = false,
}: ClosingStatusChipProps) {
  const statusColor = STATUS_COLOR[status];

  return (
    <Chip
      size="small"
      variant="outlined"
      icon={<CircleIcon sx={{ fontSize: STATUS_DOT_SIZE }} />}
      label={
        spelledOut
          ? CLOSING_STATUS_LABEL[status]
          : CLOSING_STATUS_SHORT_LABEL[status]
      }
      sx={{
        fontWeight: 600,
        color: statusColor,
        borderColor: (theme) => alpha(statusColor(theme), BORDER_OPACITY),
        bgcolor: (theme) => alpha(statusColor(theme), SURFACE_OPACITY),
        "& .MuiChip-icon": { color: "inherit" },
      }}
    />
  );
}
